// Turns a row from TaxiCaller's Customer Account Report (captured onto
// crm_account_pending_creations.taxicaller_details by the nightly
// check-taxicaller-account-changes function) into crm_accounts columns, so
// creating an account from Closed Check -> New Accounts starts pre-filled
// instead of as a blank "Unnamed - <id>" stub.
//
// Only fields with a clear 1:1 meaning are mapped; everything stays editable
// in the CRM form that opens straight after.
import { ACCOUNT_SERIES } from '@/lib/accountSeries';

export type TaxiCallerAccountRow = Record<string, string>;

const clean = (v: string | undefined): string | null => (v ?? '').trim() || null;

// TaxiCaller wraps a required-field value in quotes -- "ACC Transport Assist"
// is one fixed value, whereas "ANZ","Massey" is a pick-list of several.
const singleQuotedValue = (v: string | undefined): string | null => {
  const m = /^"([^"]*)"$/.exec((v ?? '').trim());
  return m ? m[1].trim() || null : null;
};

const looksLikeEmail = (v: string | null): v is string => !!v && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export function crmFieldsFromTaxiCaller(
  accountId: string,
  row: TaxiCallerAccountRow | null | undefined
): Record<string, unknown> {
  const fields: Record<string, unknown> = {};

  // Billed CRM series (5-9): series type + billed status follow from the
  // account number alone, same rule as CRMAccountNumberPicker -- unless
  // TaxiCaller explicitly says the account is Direct.
  const isDirect = clean(row?.['Payment type (Billed/Direct)']) === 'Direct';
  const base = accountId.trim().split('-')[0];
  if (/^\d{4,}$/.test(base)) {
    const series = ACCOUNT_SERIES.find((s) => s.key === base.charAt(0));
    if (series && !isDirect) {
      fields.account_series_type = series.type;
      fields.billed_status = 'Billed';
    }
  }

  if (!row) return fields;

  const name = clean(row['Customer']);
  if (name) fields.account_name = name;

  const phone = clean(row['Phone']);
  if (phone) fields.phone = phone;

  const billingEmail = clean(row['Billing email']);
  if (looksLikeEmail(billingEmail)) {
    fields.billing_email = billingEmail;
    fields.invoice_email = billingEmail;
  }

  // TaxiCaller's "Contact email" sometimes holds a name rather than an address.
  const contactEmail = clean(row['Contact email']);
  if (looksLikeEmail(contactEmail)) fields.email = contactEmail;

  const status = clean(row['Account status']);
  if (status) fields.is_active = status.toLowerCase() === 'active';

  const paymentType = clean(row['Payment type (Billed/Direct)']);
  if (paymentType === 'Billed' || paymentType === 'Direct') fields.account_type = paymentType;

  const required: [string, 'requires_project' | 'requires_cost_code' | 'requires_reference', 'default_project' | 'default_cost_code' | 'default_reference'][] = [
    ['Required field - Project', 'requires_project', 'default_project'],
    ['Required field - Cost Code', 'requires_cost_code', 'default_cost_code'],
    ['Required field - Reference', 'requires_reference', 'default_reference'],
  ];
  for (const [column, requiresField, defaultField] of required) {
    const raw = clean(row[column]);
    if (!raw) continue;
    fields[requiresField] = true;
    const single = singleQuotedValue(raw);
    if (single) fields[defaultField] = single;
  }

  return fields;
}

/** Short "Billed · 067775410" style summary for the New Accounts list. */
export function taxiCallerSummary(row: TaxiCallerAccountRow | null | undefined): string | null {
  if (!row) return null;
  const parts = [clean(row['Payment type (Billed/Direct)']), clean(row['Phone'])].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}
